import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FeatureId, getFeature } from '@/config/features';

interface LockedOverlayProps {
  /**
   * The feature that is locked
   */
  feature: FeatureId;

  /**
   * Optional custom upgrade message to override the default
   */
  customMessage?: string;

  /**
   * Optional custom upgrade URL (defaults to /pricing)
   */
  upgradeUrl?: string;

  /**
   * Optional callback when upgrade button is clicked
   */
  onUpgrade?: () => void;

  /**
   * Optional className for the overlay container
   */
  className?: string;
}

/**
 * Reusable overlay component that displays a lock message for premium features
 *
 * @example
 * ```tsx
 * <div className="relative">
 *   {isLocked && <LockedOverlay feature="BRANDING" />}
 *   <YourComponent />
 * </div>
 * ```
 */
export const LockedOverlay = ({
  feature,
  customMessage,
  upgradeUrl = '/pricing',
  onUpgrade,
  className = '',
}: LockedOverlayProps) => {
  const navigate = useNavigate();
  const featureConfig = getFeature(feature);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate(upgradeUrl);
    }
  };

  const message = customMessage || featureConfig.upgradeMessage;

  return (
    <div
      className={`absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg ${className}`}
    >
      <div className="text-center p-6 max-w-sm">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] rounded-full flex items-center justify-center">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Premium Feature</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <Button
          onClick={handleUpgrade}
          className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
        >
          Upgrade to Premium
        </Button>
      </div>
    </div>
  );
};
